import { Link, Outlet } from 'react-router-dom';
import { useAuthStore, useThemeStore } from '@/stores';
import { useLogout, useCurrentUser } from '@/hooks';
import { Button, CommandPalette, useGlobalShortcuts } from '@/components/ui';
import {
  Menu,
  X,
  Sun,
  Moon,
  LogOut,
  User,
  FileText,
  Search,
  Shield,
  Key,
  Star,
  Bell,
} from 'lucide-react';
import { useState, useEffect, useRef } from 'react';

export function Layout() {
  const { isAuthenticated, user } = useAuthStore();
  const { theme, toggleTheme } = useThemeStore();
  const logout = useLogout();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { isPaletteOpen, setIsPaletteOpen } = useGlobalShortcuts();
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Fetch current user if authenticated
  useCurrentUser();

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };

    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userMenuOpen]);

  const handleLogout = () => {
    setUserMenuOpen(false);
    logout.mutate();
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto flex h-14 items-center justify-between px-4">
          {/* Logo and Navigation */}
          <div className="flex items-center space-x-6">
            <Link to="/" className="flex items-center space-x-2">
              <FileText className="h-6 w-6 text-primary" />
              <span className="font-bold text-lg">Wiki</span>
            </Link>

            <nav className="hidden md:flex items-center space-x-4">
              <Link
                to="/"
                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                首页
              </Link>
              <Link
                to="/pages"
                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                所有页面
              </Link>
              <Link
                to="/api-docs"
                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                API 文档
              </Link>
              {isAuthenticated && (
                <Link
                  to="/spaces"
                  className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  空间
                </Link>
              )}
              {isAuthenticated && (user?.role === 'editor' || user?.role === 'admin') && (
                <Link
                  to="/pages/new"
                  className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  新建页面
                </Link>
              )}
            </nav>
          </div>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Command Palette Button */}
            <Button
              variant="ghost"
              size="sm"
              className="gap-2 text-muted-foreground"
              onClick={() => setIsPaletteOpen(true)}
            >
              <Search className="h-4 w-4" />
              <span className="text-sm">搜索</span>
              <kbd className="hidden lg:inline-flex h-5 items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px]">
                ⌘K
              </kbd>
            </Button>

            <Button variant="ghost" size="icon" onClick={toggleTheme}>
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>

            {isAuthenticated && user ? (
              <div className="relative" ref={userMenuRef}>
                <Button
                  variant="ghost"
                  className="flex items-center space-x-2"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                >
                  <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground">
                    {user.username.charAt(0).toUpperCase()}
                  </div>
                  <span className="text-sm">{user.username}</span>
                </Button>

                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-card border">
                    <div className="py-1">
                      <Link
                        to="/profile"
                        className="flex items-center px-4 py-2 text-sm hover:bg-accent"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <User className="h-4 w-4 mr-2" />
                        个人资料
                      </Link>
                      <Link
                        to="/profile/api-keys"
                        className="flex items-center px-4 py-2 text-sm hover:bg-accent"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <Key className="h-4 w-4 mr-2" />
                        API Keys
                      </Link>
                      <Link
                        to="/profile/favorites"
                        className="flex items-center px-4 py-2 text-sm hover:bg-accent"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <Star className="h-4 w-4 mr-2" />
                        收藏夹
                      </Link>
                      <Link
                        to="/notifications"
                        className="flex items-center px-4 py-2 text-sm hover:bg-accent"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <Bell className="h-4 w-4 mr-2" />
                        通知
                      </Link>
                      {user.role === 'admin' && (
                        <Link
                          to="/admin"
                          className="flex items-center px-4 py-2 text-sm hover:bg-accent"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          <Shield className="h-4 w-4 mr-2" />
                          管理面板
                        </Link>
                      )}
                      <button
                        onClick={handleLogout}
                        className="flex items-center w-full px-4 py-2 text-sm hover:bg-accent text-destructive"
                      >
                        <LogOut className="h-4 w-4 mr-2" />
                        登出
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <Link to="/login">
                  <Button variant="ghost">登录</Button>
                </Link>
                <Link to="/register">
                  <Button>注册</Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="flex md:hidden items-center space-x-2">
            <Button variant="ghost" size="icon" onClick={toggleTheme}>
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t">
            <div className="container mx-auto px-4 py-4 space-y-4">
              <nav className="space-y-2">
                <Link
                  to="/"
                  className="block py-2 text-sm font-medium hover:text-primary"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  首页
                </Link>
                <Link
                  to="/pages"
                  className="block py-2 text-sm font-medium hover:text-primary"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  所有页面
                </Link>
                <Link
                  to="/api-docs"
                  className="block py-2 text-sm font-medium hover:text-primary"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  API 文档
                </Link>
                {isAuthenticated && (
                  <Link
                    to="/spaces"
                    className="block py-2 text-sm font-medium hover:text-primary"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    空间
                  </Link>
                )}
                <Link
                  to="/search"
                  className="block py-2 text-sm font-medium hover:text-primary"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  搜索
                </Link>
                {isAuthenticated && (user?.role === 'editor' || user?.role === 'admin') && (
                  <Link
                    to="/pages/new"
                    className="block py-2 text-sm font-medium hover:text-primary"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    新建页面
                  </Link>
                )}
              </nav>

              {isAuthenticated && user ? (
                <div className="pt-4 border-t space-y-2">
                  <div className="flex items-center space-x-2 py-2">
                    <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground">
                      {user.username.charAt(0).toUpperCase()}
                    </div>
                    <span className="text-sm font-medium">{user.username}</span>
                  </div>
                  <Link
                    to="/profile"
                    className="block py-2 text-sm hover:text-primary"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    个人资料
                  </Link>
                  <Link
                    to="/profile/api-keys"
                    className="block py-2 text-sm hover:text-primary"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    API Keys
                  </Link>
                  <Link
                    to="/profile/favorites"
                    className="block py-2 text-sm hover:text-primary"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    收藏夹
                  </Link>
                  <Link
                    to="/notifications"
                    className="block py-2 text-sm hover:text-primary"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    通知
                  </Link>
                  {user.role === 'admin' && (
                    <Link
                      to="/admin"
                      className="block py-2 text-sm hover:text-primary"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      管理面板
                    </Link>
                  )}
                  <button
                    onClick={handleLogout}
                    className="block py-2 text-sm text-destructive hover:text-destructive/80"
                  >
                    登出
                  </button>
                </div>
              ) : (
                <div className="pt-4 border-t flex space-x-2">
                  <Link to="/login" className="flex-1" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="outline" className="w-full">登录</Button>
                  </Link>
                  <Link to="/register" className="flex-1" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full">注册</Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t py-6 mt-auto">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>Wiki &copy; {new Date().getFullYear()} - Powered by Cloudflare Workers</p>
        </div>
      </footer>

      {/* Command Palette */}
      <CommandPalette
        isOpen={isPaletteOpen}
        onClose={() => setIsPaletteOpen(false)}
      />
    </div>
  );
}
